/* eslint-disable no-nested-ternary */
import { FC } from 'react';

import { useAppSelector } from '@core/store';

import { Icon, Spinner } from '..';
import { BulkDeleteJob, selectBulkDeleteJobs } from './BulkDeleteSlice';

const memberLabel = (count: number) =>
  `${count} ${count === 1 ? 'member' : 'members'}`;

const getCopy = (job: BulkDeleteJob) => {
  switch (job.status) {
    case 'success':
      return {
        title: `${memberLabel(job.count)} deleted`,
        description: `Selected members have been removed from this ${job.level}`
      };
    case 'error':
      return {
        title: `Couldn't delete ${memberLabel(job.count)}`,
        description: 'Something went wrong. Please try again.'
      };
    default:
      return {
        title: `Deleting ${memberLabel(job.count)}`,
        description:
          'Removing members and disabling their active payment links. This may take some time.'
      };
  }
};

const TrashIcon = () => <Icon name="trash-icon-with-rounded-bg" size="48" />;

const CheckIcon = () => <Icon name="green-tick-with-rounded-bg" size="48" />;

const AlertIcon = () => <Icon name="rounded-unlink-with-bg" size="48" />;

const JobRow: FC<{ job: BulkDeleteJob }> = ({ job }) => {
  const { title, description } = getCopy(job);
  const isDone = job.status === 'success';
  const isError = job.status === 'error';

  return (
    <div className="flex w-full items-center gap-6">
      <div className="flex flex-1 items-center gap-3">
        <div className="flex shrink-0 items-center justify-center rounded-full">
          {isDone ? <CheckIcon /> : isError ? <AlertIcon /> : <TrashIcon />}
        </div>
        <div className="flex flex-col justify-center gap-1">
          <p className="text-base font-semibold leading-[120%] text-cofeeNeutral10">
            {title}
          </p>
          <p className="text-sm leading-[18px] text-cofeeNeutral8">
            {description}
          </p>
        </div>
      </div>
      {job.status === 'deleting' && <Spinner className="!size-8 !border-4" />}
    </div>
  );
};

const BulkDeleteProgressToast: FC = () => {
  const jobs = useAppSelector(selectBulkDeleteJobs);

  if (jobs.length === 0) return null;

  const isStacked = jobs.length > 1;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed bottom-6 right-6 z-[1100] flex w-[459.5px] max-w-[calc(100vw-48px)] flex-col justify-center rounded-xl bg-white ${
        isStacked
          ? 'gap-4 p-6 shadow-[0px_0px_12px_3px_rgba(10,13,18,0.06)]'
          : 'px-4 py-6 shadow-[0px_20px_24px_-4px_rgba(10,13,18,0.08),0px_8px_8px_-4px_rgba(10,13,18,0.03)]'
      }`}
    >
      {jobs.map((job, index) => (
        <div key={job.id} className="flex flex-col gap-4">
          {index > 0 && (
            <hr className="m-0 w-full border-t border-cofeeGreyBlue5" />
          )}
          <JobRow job={job} />
        </div>
      ))}
    </div>
  );
};

export default BulkDeleteProgressToast;
